export type MobileLifecycleReason =
  | 'capacitor-active'
  | 'capacitor-background'
  | 'visibility'
  | 'pagehide'
  | 'pageshow'
  | 'freeze'
  | 'resume'
  | 'focus'
  | 'online'
  | 'offline';

export interface MobileLifecycleCallbacks {
  onForeground: (reason: MobileLifecycleReason) => void;
  onBackground: (reason: MobileLifecycleReason) => void;
  onNetworkChange: (online: boolean, reason: MobileLifecycleReason) => void;
}

export interface MobileLifecycleHandle {
  stop(): void;
  isForeground(): boolean;
  isOnline(): boolean;
}

/**
 * Collapses Capacitor and browser lifecycle events into one foreground stream.
 * A single resume can produce appStateChange, visibilitychange, pageshow,
 * resume and focus; only one recovery callback is emitted for that burst.
 */
export function installMobileLifecycleCoordinator(callbacks: MobileLifecycleCallbacks): MobileLifecycleHandle {
  let stopped = false;
  let pageVisible = typeof document === 'undefined' || document.visibilityState !== 'hidden';
  let capacitorActive: boolean | null = null;
  let frozen = false;
  let online = typeof navigator === 'undefined' || navigator.onLine !== false;
  let foreground = pageVisible;
  let lastForegroundSignalAt = 0;
  let foregroundTimer: ReturnType<typeof setTimeout> | null = null;
  let capacitorListener: { remove: () => Promise<void> | void } | null = null;

  const effectiveForeground = () => !frozen && pageVisible && capacitorActive !== false;

  const clearForegroundTimer = () => {
    if (!foregroundTimer) return;
    clearTimeout(foregroundTimer);
    foregroundTimer = null;
  };

  const emitState = (reason: MobileLifecycleReason, allowRecoverySignal = false) => {
    if (stopped) return;
    const nextForeground = effectiveForeground();
    if (!nextForeground) {
      clearForegroundTimer();
      if (foreground) {
        foreground = false;
        callbacks.onBackground(reason);
      }
      return;
    }

    const now = Date.now();
    const shouldRecover = !foreground || allowRecoverySignal;
    if (!shouldRecover || now - lastForegroundSignalAt < 220) return;
    clearForegroundTimer();
    foregroundTimer = setTimeout(() => {
      foregroundTimer = null;
      if (stopped || !effectiveForeground()) return;
      const emitNow = !foreground || Date.now() - lastForegroundSignalAt >= 220;
      foreground = true;
      if (!emitNow) return;
      lastForegroundSignalAt = Date.now();
      callbacks.onForeground(reason);
    }, 100);
  };

  const onVisibility = () => {
    pageVisible = document.visibilityState !== 'hidden';
    emitState('visibility', pageVisible);
  };
  const onPageHide = () => {
    pageVisible = false;
    emitState('pagehide');
  };
  const onPageShow = () => {
    pageVisible = true;
    frozen = false;
    emitState('pageshow', true);
  };
  const onFreeze = () => {
    frozen = true;
    emitState('freeze');
  };
  const onResume = () => {
    frozen = false;
    pageVisible = document.visibilityState !== 'hidden';
    emitState('resume', true);
  };
  const onFocus = () => {
    pageVisible = document.visibilityState !== 'hidden';
    emitState('focus', true);
  };
  const onOnline = () => {
    if (online) {
      emitState('online', true);
      return;
    }
    online = true;
    callbacks.onNetworkChange(true, 'online');
    emitState('online', true);
  };
  const onOffline = () => {
    if (!online) return;
    online = false;
    callbacks.onNetworkChange(false, 'offline');
  };

  if (typeof window !== 'undefined') {
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    window.addEventListener('focus', onFocus);
    window.addEventListener('pagehide', onPageHide);
    window.addEventListener('pageshow', onPageShow);
  }
  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', onVisibility);
    document.addEventListener('freeze', onFreeze);
    document.addEventListener('resume', onResume);
  }

  void import('@capacitor/app')
    .then(({ App }) => App.addListener('appStateChange', ({ isActive }) => {
      capacitorActive = isActive;
      if (isActive) {
        pageVisible = typeof document === 'undefined' || document.visibilityState !== 'hidden';
        frozen = false;
        emitState('capacitor-active', true);
      } else {
        emitState('capacitor-background');
      }
    }))
    .then((listener) => {
      if (stopped) void listener.remove();
      else capacitorListener = listener;
    })
    .catch(() => undefined);

  return {
    stop() {
      if (stopped) return;
      stopped = true;
      clearForegroundTimer();
      if (typeof window !== 'undefined') {
        window.removeEventListener('online', onOnline);
        window.removeEventListener('offline', onOffline);
        window.removeEventListener('focus', onFocus);
        window.removeEventListener('pagehide', onPageHide);
        window.removeEventListener('pageshow', onPageShow);
      }
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', onVisibility);
        document.removeEventListener('freeze', onFreeze);
        document.removeEventListener('resume', onResume);
      }
      void capacitorListener?.remove();
    },
    isForeground: () => foreground && effectiveForeground(),
    isOnline: () => online
  };
}
